/**
 * Guía del portal: lógica pura del asistente de ayuda que se monta en cualquier portal.
 *
 * Habla con la Edge Function `ia-guia-portal` (contrato fijo, JSON sin streaming); cada
 * llamada lleva el `agente` (código de `ia_agentes`). Qué agente toca en cada pantalla lo
 * decide la RPC `ia_agentes_disponibles`: el de tipo `guia` cuyo `ruta_portal` es prefijo de
 * la ruta actual. Si no hay ninguno, el widget no se pinta; esa RPC ya filtra por rol, así que
 * es también el gate de permiso. Un portal nuevo solo necesita su fila en BD.
 *
 * Aquí vive todo lo que no depende de React: elegir el agente, leer el cuerpo de un error,
 * validar las rutas que propone el modelo, armar y restaurar el estado de la conversación.
 *
 * El asistente solo consulta; nunca cambia datos. Las rutas que devuelve se navegan con
 * react-router, así que cualquier cosa que no sea una ruta interna del panel se descarta.
 */

import {
  mensajesIaDesdeBitacora,
  normalizarConversacionesIa,
  type ConversacionIa,
} from "./chatIa";

export const GUIA_FUNCION = "ia-guia-portal";

/** Tope del mensaje que acepta la función. */
export const MAX_MENSAJE = 500;

/** Desde cuántos caracteres se muestra el contador del input. */
export const UMBRAL_CONTADOR_CARACTERES = 400;

/** Desde cuántas preguntas restantes se muestra el aviso del pie. */
export const UMBRAL_RESTANTES = 10;

/** Si el endpoint de sugerencias falla, se ofrecen estas. Genéricas: sirven en cualquier portal. */
export const SUGERENCIAS_RESPALDO: readonly string[] = [
  "¿Qué puedo hacer en este portal?",
  "¿Dónde encuentro un reporte?",
  "¿Cómo busco un registro?",
  "¿Qué significa cada estatus?",
];

const MAX_SUGERENCIAS = 6;

// ── Agente que corresponde a la pantalla ────────────────────────────────────

export interface AgenteDisponible {
  codigo: string;
  nombre: string;
  descripcion: string;
  tipo: string;
  rutaPortal: string;
  /** Preguntas sugeridas editables en Asistentes IA; vacío si la RPC aún no las devuelve. */
  sugerencias: string[];
}

/** Filas de `ia_agentes_disponibles()` tipadas; descarta las que no traen código o ruta. */
export function normalizarAgentesDisponibles(data: unknown): AgenteDisponible[] {
  if (!Array.isArray(data)) return [];
  const out: AgenteDisponible[] = [];
  for (const f of data) {
    if (!esObjeto(f)) continue;
    const codigo = typeof f.codigo === "string" ? f.codigo.trim() : "";
    const rutaPortal = typeof f.ruta_portal === "string" ? f.ruta_portal.trim() : "";
    if (!codigo) continue;
    out.push({
      codigo,
      nombre: typeof f.nombre === "string" && f.nombre.trim() ? f.nombre.trim() : "Guía del portal",
      descripcion: typeof f.descripcion === "string" ? f.descripcion.trim() : "",
      tipo: typeof f.tipo === "string" ? f.tipo : "",
      rutaPortal,
      sugerencias: Array.isArray(f.sugerencias)
        ? f.sugerencias.filter((x): x is string => typeof x === "string" && x.trim() !== "").map((x) => x.trim())
        : [],
    });
  }
  return out;
}

/** Quita la barra final para comparar prefijos (`/admin/portal-cobranza/` = `/admin/portal-cobranza`). */
const sinBarraFinal = (r: string) => (r.length > 1 && r.endsWith("/") ? r.replace(/\/+$/, "") : r);

/**
 * `true` si `ruta` está dentro de `prefijo` respetando segmentos:
 * `/admin/portal-cobranza` cubre `/admin/portal-cobranza/inmuebles`, no `/admin/portal-cobranzas`.
 */
export function rutaDentroDe(ruta: string, prefijo: string): boolean {
  const p = sinBarraFinal(prefijo.trim());
  if (!p.startsWith("/admin")) return false;
  const r = sinBarraFinal(ruta);
  return r === p || r.startsWith(`${p}/`);
}

/**
 * El agente del `tipo` pedido (por omisión `guia`) cuyo `ruta_portal` cubre la ruta; si varios, el
 * de prefijo más largo. Nunca devuelve uno de otro tipo: la guía y el analista de un mismo portal
 * conviven sin pisarse.
 */
export function agenteParaRuta(
  agentes: AgenteDisponible[],
  pathname: string,
  tipo: string = "guia",
): AgenteDisponible | null {
  let mejor: AgenteDisponible | null = null;
  for (const a of agentes) {
    if (a.tipo !== tipo || !a.rutaPortal || !rutaDentroDe(pathname, a.rutaPortal)) continue;
    if (!mejor || sinBarraFinal(a.rutaPortal).length > sinBarraFinal(mejor.rutaPortal).length) mejor = a;
  }
  return mejor;
}

export type GuiaErrorCodigo =
  | "sin_permiso"
  | "limite_alcanzado"
  | "deshabilitado"
  | "mensaje_invalido"
  | "error_modelo";

const CODIGOS: readonly GuiaErrorCodigo[] = [
  "sin_permiso",
  "limite_alcanzado",
  "deshabilitado",
  "mensaje_invalido",
  "error_modelo",
];

export interface GuiaEnlace {
  etiqueta: string;
  ruta: string;
}

export interface GuiaRespuesta {
  respuesta: string;
  idConversacion: number;
  enlaces: GuiaEnlace[];
  desdeCache: boolean;
  restantesHora: number | null;
  version: string;
}

export interface GuiaError {
  codigo: GuiaErrorCodigo;
  mensaje: string;
  reintentarEnS: number | null;
}

export interface MensajeGuia {
  id: string;
  rol: "usuario" | "asistente";
  texto: string;
  enlaces: GuiaEnlace[];
}

export interface EstadoGuia {
  mensajes: MensajeGuia[];
  idConversacion: number | null;
  restantesHora: number | null;
  /** Epoch ms hasta el que el input queda bloqueado por `limite_alcanzado`. */
  bloqueadoHasta: number | null;
}

const MENSAJES_POR_CODIGO: Record<GuiaErrorCodigo, string> = {
  sin_permiso: "No tienes acceso a la guía.",
  limite_alcanzado: "Llegaste al límite de preguntas de esta hora.",
  deshabilitado: "La guía no está disponible por ahora.",
  mensaje_invalido: "No pude leer tu pregunta. Revisa el texto e inténtalo de nuevo.",
  error_modelo: "No pude responder en este momento.",
};

const esObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const numeroFinito = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/**
 * `true` solo para rutas internas del panel: empiezan con `/admin/`, sin protocolo, sin `//`
 * (URL relativa al protocolo) ni barras invertidas, y sin caracteres de control.
 */
export function esRutaInterna(ruta: unknown): ruta is string {
  if (typeof ruta !== "string") return false;
  const r = ruta.trim();
  if (!r.startsWith("/admin/") || r.length <= "/admin/".length) return false;
  if (r.includes("//") || r.includes("\\")) return false;
  for (let i = 0; i < r.length; i++) {
    const c = r.charCodeAt(i);
    if (c <= 0x20 || c === 0x7f) return false;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(r)) return false;
  return true;
}

/** Deja solo los enlaces bien formados con ruta interna, sin repetidos. */
export function sanearEnlaces(enlaces: unknown): GuiaEnlace[] {
  if (!Array.isArray(enlaces)) return [];
  const vistos = new Set<string>();
  const out: GuiaEnlace[] = [];
  for (const e of enlaces) {
    if (!esObjeto(e)) continue;
    const etiqueta = typeof e.etiqueta === "string" ? e.etiqueta.trim() : "";
    if (!etiqueta || !esRutaInterna(e.ruta)) continue;
    const ruta = (e.ruta as string).trim();
    if (vistos.has(ruta)) continue;
    vistos.add(ruta);
    out.push({ etiqueta, ruta });
  }
  return out;
}

/** Convierte el 200 de la función en una respuesta tipada, o `null` si no cumple el contrato. */
export function normalizarRespuesta(data: unknown): GuiaRespuesta | null {
  if (!esObjeto(data)) return null;
  const respuesta = typeof data.respuesta === "string" ? data.respuesta.trim() : "";
  const idConversacion = numeroFinito(data.id_conversacion);
  if (!respuesta || idConversacion === null) return null;
  const restantes = numeroFinito(data.restantes_hora);
  return {
    respuesta,
    idConversacion,
    enlaces: sanearEnlaces(data.enlaces),
    desdeCache: data.desde_cache === true,
    restantesHora: restantes === null ? null : Math.max(0, Math.floor(restantes)),
    version: typeof data.version === "string" ? data.version : "",
  };
}

/** Lee el cuerpo `{ error, mensaje, reintentar_en_s }`. Un código desconocido cuenta como `error_modelo`. */
export function parsearCuerpoError(body: unknown): GuiaError {
  const codigoCrudo = esObjeto(body) ? body.error : undefined;
  const codigo: GuiaErrorCodigo = CODIGOS.includes(codigoCrudo as GuiaErrorCodigo)
    ? (codigoCrudo as GuiaErrorCodigo)
    : "error_modelo";
  const mensajeCrudo = esObjeto(body) && typeof body.mensaje === "string" ? body.mensaje.trim() : "";
  const segundos = esObjeto(body) ? numeroFinito(body.reintentar_en_s) : null;
  return {
    codigo,
    mensaje: mensajeCrudo || MENSAJES_POR_CODIGO[codigo],
    reintentarEnS: segundos !== null && segundos > 0 ? segundos : null,
  };
}

/**
 * Error de `supabase.functions.invoke`: el cuerpo real viene en `error.context` (un `Response`).
 * Se lee de forma defensiva; si no hay cuerpo legible, cae a `error_modelo`.
 */
export async function leerErrorGuia(err: unknown): Promise<GuiaError> {
  const ctx = esObjeto(err) ? err.context : undefined;
  if (typeof ctx === "object" && ctx !== null) {
    const res = ctx as { clone?: () => unknown; json?: () => Promise<unknown> };
    try {
      const copia = (typeof res.clone === "function" ? res.clone() : res) as { json?: () => Promise<unknown> };
      if (typeof copia.json === "function") {
        return parsearCuerpoError(await copia.json());
      }
    } catch {
      // Cuerpo vacío, no JSON o ya consumido: se trata como fallo del modelo.
    }
  }
  return parsearCuerpoError(null);
}

/** Minutos (redondeo hacia arriba, mínimo 1) que faltan para `hasta`. */
export function minutosRestantes(hasta: number, ahora: number): number {
  return Math.max(1, Math.ceil((hasta - ahora) / 60_000));
}

export function estaBloqueado(estado: EstadoGuia, ahora: number): boolean {
  return estado.bloqueadoHasta !== null && estado.bloqueadoHasta > ahora;
}

export function mostrarRestantes(restantes: number | null): restantes is number {
  return restantes !== null && restantes <= UMBRAL_RESTANTES;
}

export function textoRestantes(restantes: number): string {
  if (restantes <= 0) return "Ya no te quedan preguntas esta hora";
  return restantes === 1 ? "Te queda 1 pregunta esta hora" : `Te quedan ${restantes} preguntas esta hora`;
}

export function mostrarContadorCaracteres(largo: number): boolean {
  return largo >= UMBRAL_CONTADOR_CARACTERES;
}

/** Mensaje listo para enviar, o `null` si está vacío. Recorta al tope de la función. */
export function prepararMensaje(texto: string): string | null {
  const t = texto.trim();
  if (!t) return null;
  return t.slice(0, MAX_MENSAJE);
}

/** Sugerencias del endpoint, o las de respaldo si no llegó nada usable. */
export function sanearSugerencias(data: unknown): string[] {
  const lista = esObjeto(data) && Array.isArray(data.sugerencias) ? data.sugerencias : [];
  const out = [
    ...new Set(
      lista
        .filter((s): s is string => typeof s === "string")
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s.length <= MAX_MENSAJE),
    ),
  ].slice(0, MAX_SUGERENCIAS);
  return out.length > 0 ? out : [...SUGERENCIAS_RESPALDO];
}

// ── Estado de la conversación ───────────────────────────────────────────────

export function estadoInicial(): EstadoGuia {
  return { mensajes: [], idConversacion: null, restantesHora: null, bloqueadoHasta: null };
}

export function conMensajeUsuario(estado: EstadoGuia, texto: string, id: string): EstadoGuia {
  return { ...estado, mensajes: [...estado.mensajes, { id, rol: "usuario", texto, enlaces: [] }] };
}

export function conRespuesta(estado: EstadoGuia, resp: GuiaRespuesta, id: string): EstadoGuia {
  return {
    ...estado,
    mensajes: [...estado.mensajes, { id, rol: "asistente", texto: resp.respuesta, enlaces: resp.enlaces }],
    idConversacion: resp.idConversacion,
    restantesHora: resp.restantesHora ?? estado.restantesHora,
    bloqueadoHasta: null,
  };
}

/** Quita el último mensaje si es del usuario (pregunta que la función no procesó). */
export function sinUltimoMensajeUsuario(estado: EstadoGuia): EstadoGuia {
  const ultimo = estado.mensajes[estado.mensajes.length - 1];
  if (!ultimo || ultimo.rol !== "usuario") return estado;
  return { ...estado, mensajes: estado.mensajes.slice(0, -1) };
}

/** Sin `reintentar_en_s`, se bloquea una hora: es la ventana del límite. */
export function conBloqueo(estado: EstadoGuia, reintentarEnS: number | null, ahora: number): EstadoGuia {
  const segundos = reintentarEnS ?? 3600;
  return { ...estado, restantesHora: 0, bloqueadoHasta: ahora + segundos * 1000 };
}

// ── Persistencia en sessionStorage ──────────────────────────────────────────

const CLAVE_ESTADO = "sozu.guia-portal.v1";
const CLAVE_OCULTA = "sozu.guia-portal.oculta.v1";

/** Claves de `sessionStorage` por usuario Y agente: dos portales no comparten conversación. */
export function clavesGuia(usuario: string, agente: string) {
  return { estado: `${CLAVE_ESTADO}:${usuario}:${agente}`, oculta: `${CLAVE_OCULTA}:${usuario}:${agente}` };
}

/** Tope de mensajes que se guardan, para no llenar el storage con una charla larga. */
const MAX_MENSAJES_GUARDADOS = 60;

export function serializarEstado(estado: EstadoGuia): string {
  return JSON.stringify({ ...estado, mensajes: estado.mensajes.slice(-MAX_MENSAJES_GUARDADOS) });
}

/** Restaura lo guardado; ante cualquier cosa rara devuelve el estado inicial. */
export function restaurarEstado(raw: string | null): EstadoGuia {
  if (!raw) return estadoInicial();
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return estadoInicial();
  }
  if (!esObjeto(data) || !Array.isArray(data.mensajes)) return estadoInicial();
  const mensajes: MensajeGuia[] = [];
  for (const m of data.mensajes) {
    if (!esObjeto(m)) continue;
    if (typeof m.id !== "string" || typeof m.texto !== "string") continue;
    if (m.rol !== "usuario" && m.rol !== "asistente") continue;
    mensajes.push({ id: m.id, rol: m.rol, texto: m.texto, enlaces: sanearEnlaces(m.enlaces) });
  }
  return {
    mensajes,
    idConversacion: numeroFinito(data.idConversacion),
    restantesHora: numeroFinito(data.restantesHora),
    bloqueadoHasta: numeroFinito(data.bloqueadoHasta),
  };
}

// ── Historial de conversaciones ─────────────────────────────────────────────
//
// La función ya guarda cada turno en `ia_conversaciones` / `ia_mensajes`. Lo genérico del
// historial (agrupar, buscar, fechas) vive en `lib/chatIa`, compartido con el Analista de tickets
// y el Agente CEO; aquí se re-exporta con los nombres de siempre y se ajusta a la guía (la guía
// continúa una conversación por `numero`, así que sin número no sirve).

export {
  agruparConversaciones,
  cuandoConversacion,
  filtrarConversaciones,
  type GrupoConversaciones,
} from "./chatIa";

export type ConversacionGuia = ConversacionIa & { numero: number };

/** Filas de `ia_conversaciones` tipadas; descarta las que no traen id, número o fecha. */
export function normalizarConversaciones(data: unknown): ConversacionGuia[] {
  return normalizarConversacionesIa(data).filter((c): c is ConversacionGuia => c.numero !== null);
}

/**
 * Filas de `ia_mensajes` → mensajes de la guía. Los enlaces no se guardan en la bitácora: una
 * conversación reabierta trae solo el texto (los enlaces escritos dentro del markdown siguen
 * funcionando).
 */
export function mensajesDesdeBitacora(filas: unknown): MensajeGuia[] {
  return mensajesIaDesdeBitacora(filas).map((m) => ({ id: m.id, rol: m.rol, texto: m.texto, enlaces: [] }));
}

/** Estado del panel al reabrir una conversación: conserva el límite de la hora, que no es de la charla. */
export function estadoDesdeConversacion(
  actual: EstadoGuia,
  numero: number,
  mensajes: MensajeGuia[],
): EstadoGuia {
  return { ...actual, mensajes, idConversacion: numero };
}
